import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import {
  AlertsCountLabel,
  EmptyState,
  EmptyStateIcon,
  PrimaryText,
  SecondaryText,
  List as BaseList,
} from './Common';
import { formatDate } from './Common/dateHelpers';

const List = styled(BaseList)`
  margin: 0 0 ${pxToRem(16)} 0;
  width: 100%;
`;

const Row = styled.li`
  padding: ${pxToRem(12)} ${pxToRem(16)};
  margin: 0 ${pxToRem(-16)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};

  &:last-child {
    border-bottom: none;
  }
`;

const AlertPrice = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 500;
  color: ${themeColors.colorTextPrimary};
  margin: 0 0 ${pxToRem(4)} 0;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-family: ${variables.family.goldSilver};
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
  }
`;

const MobileBreak = styled.br`
  display: none;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    display: block;
  }
`;

const AlertMeta = styled.span`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-size: 16px;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-family: ${variables.family.goldSilver};
    font-weight: 500;
    font-size: 14px;
    line-height: 140%;
  }
`;

export const TriggeredAlertsHeader = ({ count }) => {
  const { t } = useTranslation();
  return (
    <AlertsCountLabel>
      {count > 1
        ? t('gtt.labels.alertsTriggered_other', { count })
        : t('gtt.labels.alertsTriggered_one', { count })}
    </AlertsCountLabel>
  );
};

TriggeredAlertsHeader.propTypes = {
  count: PropTypes.number,
};

const TriggeredAlerts = ({ alertsList }) => {
  const { t } = useTranslation();

  if (!alertsList || isEmpty(alertsList)) {
    const noTriggeredAlertsSub = t('gtt.labels.noTriggeredAlertsSub');
    const breakIndex = noTriggeredAlertsSub.indexOf('your') + 'your'.length;
    const firstLine = noTriggeredAlertsSub.slice(0, breakIndex);
    const secondLine = noTriggeredAlertsSub.slice(breakIndex).trim();

    return (
      <EmptyState>
        <EmptyStateIcon />
        <PrimaryText>{t('gtt.labels.noTriggeredAlerts')}</PrimaryText>
        <SecondaryText>
          {firstLine} <MobileBreak />
          {secondLine}
        </SecondaryText>
      </EmptyState>
    );
  }

  return (
    <List>
      {alertsList.map((alert, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Row key={index}>
          <AlertPrice>{alert.alertPrice}</AlertPrice>
          <AlertMeta>
            {t('gtt.labels.alertTriggeredOn', {
              date: formatDate(alert.alertDate),
              time: alert.alertTime,
            })}
          </AlertMeta>
        </Row>
      ))}
    </List>
  );
};

TriggeredAlerts.propTypes = {
  alertsList: PropTypes.arrayOf(PropTypes.shape({})),
};

export default TriggeredAlerts;
